import { companyQueryKeys, companyQueryOptions } from '@/hooks/queries/company.queries';
import { useAuth } from '@/hooks/useAuth';
import { mergeFavoriteShop, sanitizeFavoriteShop } from '@/lib/favorites';
import { getApiErrorMessage } from '@/lib/api/error';
import { CompanyService } from '@/services/company.service';
import type { FavoriteShopSnapshot } from '@/types/profile';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { toast } from 'sonner';

export function useFavorites() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const viewerId = user?.id ?? null;
  const { data: favoritesData, isLoading: isLoadingFavorites } = useQuery(
    companyQueryOptions.favorites(viewerId, Boolean(viewerId)),
  );
  const { data: favoriteCountData, isLoading: isLoadingFavoriteCount } = useQuery(
    companyQueryOptions.favoritesCount(viewerId, Boolean(viewerId)),
  );
  const favoriteItems = useMemo(() => (viewerId ? (favoritesData ?? []) : []), [favoritesData, viewerId]);

  const mutation = useMutation({
    mutationFn: async ({ shop, shouldFavorite }: { shop: FavoriteShopSnapshot; shouldFavorite: boolean }) => {
      if (shouldFavorite) {
        await CompanyService.addFavorite(shop.id);
        return;
      }

      await CompanyService.removeFavorite(shop.id);
    },
    onMutate: async ({ shop, shouldFavorite }) => {
      if (!viewerId) {
        return null;
      }

      const favoritesQueryKey = companyQueryKeys.favorites(viewerId);
      const favoriteCountQueryKey = companyQueryKeys.favoritesCount(viewerId);

      await queryClient.cancelQueries({ queryKey: favoritesQueryKey });
      await queryClient.cancelQueries({ queryKey: favoriteCountQueryKey });

      const previousFavorites = queryClient.getQueryData<FavoriteShopSnapshot[]>(favoritesQueryKey);
      const previousCount = queryClient.getQueryData<{ total: number }>(favoriteCountQueryKey);
      const sanitizedShop = sanitizeFavoriteShop(shop);

      queryClient.setQueryData<FavoriteShopSnapshot[]>(favoritesQueryKey, (current = []) => {
        if (shouldFavorite) {
          if (current.some((item) => item.id === sanitizedShop.id)) {
            return current;
          }

          return [sanitizedShop, ...current];
        }

        return current.filter((item) => item.id !== sanitizedShop.id);
      });

      queryClient.setQueryData<{ total: number }>(favoriteCountQueryKey, (current) => ({
        total: Math.max(0, (current?.total ?? previousFavorites?.length ?? 0) + (shouldFavorite ? 1 : -1)),
      }));

      return { previousCount, previousFavorites };
    },
    onError: (error, _variables, context) => {
      if (viewerId) {
        queryClient.setQueryData(companyQueryKeys.favorites(viewerId), context?.previousFavorites ?? []);
        queryClient.setQueryData(companyQueryKeys.favoritesCount(viewerId), context?.previousCount ?? { total: 0 });
      }

      toast.error('Não foi possível atualizar favoritos', {
        description: getApiErrorMessage(error, 'Tente novamente em instantes.'),
      });
    },
    onSettled: async () => {
      if (!viewerId) {
        return;
      }

      await queryClient.invalidateQueries({ queryKey: companyQueryKeys.favorites(viewerId) });
      await queryClient.invalidateQueries({ queryKey: companyQueryKeys.favoritesCount(viewerId) });
    },
  });

  const toggleFavorite = useCallback(
    (shop: FavoriteShopSnapshot | string) => {
      if (!viewerId) {
        toast.error('Entre para salvar favoritos', {
          description: 'Faça login para sincronizar seus locais favoritos.',
        });
        return;
      }

      const nextShop = sanitizeFavoriteShop(typeof shop === 'string' ? { id: shop } : shop);
      const hasFavorite = favoriteItems.some((item) => item.id === nextShop.id);

      mutation.mutate({
        shop: nextShop,
        shouldFavorite: !hasFavorite,
      });
    },
    [favoriteItems, mutation, viewerId],
  );

  const syncFavoriteItems = useCallback(
    (items: FavoriteShopSnapshot[]) => {
      if (!viewerId || items.length === 0) {
        return;
      }

      queryClient.setQueryData<FavoriteShopSnapshot[]>(companyQueryKeys.favorites(viewerId), (current = []) => {
        if (current.length === 0) {
          return current;
        }

        const nextById = new Map(items.map((item) => [item.id, sanitizeFavoriteShop(item)]));

        return current.map((currentItem) => {
          const nextItem = nextById.get(currentItem.id);

          return nextItem ? mergeFavoriteShop(currentItem, nextItem) : currentItem;
        });
      });
    },
    [queryClient, viewerId],
  );

  const favorites = favoriteItems.map((item) => item.id);
  const isFavorite = useCallback((shopId: string) => favoriteItems.some((item) => item.id === shopId), [favoriteItems]);

  return {
    favoriteItems,
    favorites,
    isFavorite,
    isLoading: isLoadingFavorites || isLoadingFavoriteCount,
    isPending: mutation.isPending,
    syncFavoriteItems,
    toggleFavorite,
    totalFavorites: viewerId ? (favoriteCountData?.total ?? favoriteItems.length) : 0,
  };
}
